import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import DataVisualizationComponent from '../Common/DataVisualizationComponent';
import { WidgetSpec } from '../../support/dataImport/presets/types';
import { widgetSpecToTileDefinition } from '../../support/dataImport/toTileDefinition';
import { IsometricTopology } from './topology/IsometricTopology';
import { TopoModel } from '../../support/dataImport/topology/topologyTypes';

/**
 * Renders one generated widget.
 *
 * Chart/table widgets are drawn by reusing the dashboard tile renderer in its inline-data mode
 * (`suppressReduxInit` + `customData`), so an imported widget is pixel-identical to the same widget once
 * it is persisted as a `CustomTile`. The `topology` widget is the one exception the tile system cannot
 * draw — it renders the ported isometric canvas from its `payload` (`TopoModel`).
 */

type Props = {
  spec: WidgetSpec;
  index?: number;
  /** Rendered height of the widget body. */
  height?: number;
};

const WidgetRendererBase: React.FC<Props> = ({ spec, index = 0, height = 320 }) => {
  const tileDefinition = React.useMemo(
    () => widgetSpecToTileDefinition(spec, index),
    [spec, index]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {spec.title}
      </Typography>
      {!!spec.note && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {spec.note}
        </Typography>
      )}
      <Box sx={{ flex: 1, minHeight: height, height }}>
        {spec.chartType === 'topology' ? (
          <IsometricTopology model={(spec.payload as TopoModel) || { version: 1, nodes: [], edges: [] }} height={height} />
        ) : (
          <DataVisualizationComponent
            tileDefinition={tileDefinition}
            suppressReduxInit
            customData={spec.data}
            queriesOverride={[]}
            transformationsOverride={[]}
            settingsOverride={spec.settings}
            displayContext="preview"
          />
        )}
      </Box>
    </Paper>
  );
};

const WidgetRenderer = React.memo(WidgetRendererBase);

export default WidgetRenderer;
